import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';

const SONG_TITLE = 'Il Cielo canta Santo';
const ARTIST_NAME = 'ComeTralci Worship';
const YOUTUBE_URL = 'https://youtu.be/mJjks8tT0-k';
const COVER_URL = '/images/worship/il-cielo-canta-santo-cover.jpg';
const PREVIEW_AUDIO_URL = '/media/worship/il-cielo-canta-santo-preview.mp3';

@Component({
  selector: 'app-il-cielo-canta-santo',
  imports: [CommonModule],
  templateUrl: './il-cielo-canta-santo.component.html',
  styleUrl: './il-cielo-canta-santo.component.scss',
})
export class IlCieloCantaSantoComponent implements OnInit, OnDestroy {
  private meta = inject(Meta);
  private title = inject(Title);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

  @ViewChild('previewAudio') previewAudio?: ElementRef<HTMLAudioElement>;

  readonly songTitle = SONG_TITLE;
  readonly artistName = ARTIST_NAME;
  readonly youtubeUrl = YOUTUBE_URL;
  readonly coverUrl = COVER_URL;
  readonly previewAudioUrl = PREVIEW_AUDIO_URL;

  // Copertina
  coverError = false;

  // Player anteprima
  audioAvailable = true;
  isPlaying = false;
  audioProgress = 0;
  audioCurrentTime = '0:00';
  audioDuration = '0:00';

  ngOnInit(): void {
    this.setSeoMeta();
    this.setJsonLd();
  }

  ngOnDestroy(): void {
    if (this.isPlaying && this.previewAudio?.nativeElement) {
      this.previewAudio.nativeElement.pause();
    }
  }

  // --- Player anteprima ---

  togglePlay(): void {
    const audio = this.previewAudio?.nativeElement;
    if (!audio) return;

    if (this.isPlaying) {
      audio.pause();
      this.isPlaying = false;
    } else {
      audio.play().then(
        () => (this.isPlaying = true),
        () => (this.audioAvailable = false),
      );
    }
  }

  onAudioTimeUpdate(): void {
    const audio = this.previewAudio?.nativeElement;
    if (!audio || !audio.duration) return;
    this.audioProgress = (audio.currentTime / audio.duration) * 100;
    this.audioCurrentTime = this.formatTime(audio.currentTime);
    this.audioDuration = this.formatTime(audio.duration);
  }

  onAudioEnded(): void {
    this.isPlaying = false;
    this.audioProgress = 0;
    this.audioCurrentTime = '0:00';
    const audio = this.previewAudio?.nativeElement;
    if (audio) audio.currentTime = 0;
  }

  onAudioError(): void {
    this.audioAvailable = false;
    this.isPlaying = false;
  }

  seekAudio(event: MouseEvent): void {
    const audio = this.previewAudio?.nativeElement;
    if (!audio || !audio.duration) return;
    const bar = event.currentTarget as HTMLElement;
    const rect = bar.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    audio.currentTime = Math.max(0, Math.min(1, ratio)) * audio.duration;
  }

  private formatTime(seconds: number): string {
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return `${min}:${sec.toString().padStart(2, '0')}`;
  }

  // --- SEO ---

  private setSeoMeta(): void {
    const pageTitle = `${SONG_TITLE} - ${ARTIST_NAME}`;
    const description = `"${SONG_TITLE}", la nuova cover live di ${ARTIST_NAME}. Guarda ora il video ufficiale su YouTube.`;
    const url = 'https://cometralci.com/worship/il-cielo-canta-santo';

    this.title.setTitle(pageTitle);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });

    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'music.song' });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({
      property: 'og:image',
      content: `https://cometralci.com${COVER_URL}`,
    });
    this.meta.updateTag({ property: 'og:site_name', content: 'Come Tralci' });
    this.meta.updateTag({ property: 'og:locale', content: 'it_IT' });

    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({
      name: 'twitter:image',
      content: `https://cometralci.com${COVER_URL}`,
    });

    this.updateCanonicalUrl(url);
  }

  private setJsonLd(): void {
    const existingScript = this.document.querySelector(
      'script#il-cielo-canta-santo-jsonld',
    );
    if (existingScript) existingScript.remove();

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'MusicRecording',
      name: SONG_TITLE,
      byArtist: {
        '@type': 'MusicGroup',
        name: ARTIST_NAME,
      },
      image: `https://cometralci.com${COVER_URL}`,
      url: 'https://cometralci.com/worship/il-cielo-canta-santo',
      sameAs: YOUTUBE_URL,
    };

    const script = this.document.createElement('script');
    script.id = 'il-cielo-canta-santo-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLd);
    this.document.head.appendChild(script);
  }

  private updateCanonicalUrl(url: string): void {
    let link = this.document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement;
    if (link) {
      link.href = url;
    } else {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      link.href = url;
      this.document.head.appendChild(link);
    }
  }
}
